import { lazy, Suspense, type ReactNode } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { SectionLoading } from "@codel/dova-ds";
import { OnboardingFlow } from "./onboarding/OnboardingFlow";
import { useMeFeatureFlags } from "./hooks/useMeFeatureFlags";
import { useMeContext } from "./MeApp";
import { HomeHubPage } from "./home/HomeHubPage";
import { PastSessionsPage } from "./sessions/PastSessionsPage";
import { ReportsPage } from "./reports/ReportsPage";
import { ReportDetailPage } from "./reports/ReportDetailPage";
import { CheckinFlow } from "./flows/checkin/CheckinFlow";
import { FeedbackFlow } from "./flows/feedback/FeedbackFlow";
import { RescheduleOfferFlow } from "./reschedule/RescheduleOfferFlow";
import { ReschedulePickPage } from "./reschedule/ReschedulePickPage";

// Lazy-loaded (codex P2): SessionCallPage's transitive
// `import "@whereby.com/browser-sdk/embed"` registers a WebRTC custom element as
// an import side-effect. Eagerly importing it here would pull that chunk into
// every /me route (onboarding, flag-off sessions) even though the feature is
// dark. Splitting it out means the SDK is fetched only when a gated, enrolled
// user actually enters the call route (the flag gate below renders BEFORE this
// Suspense child, so flag-off users never trigger the fetch).
const SessionCallPage = lazy(() =>
  import("./sessions/SessionCallPage").then((m) => ({ default: m.SessionCallPage })),
);

// Homebase routes stay shallow: reflection steps and reschedule screen states
// are component state, while shareable resources (session and offer UUIDs)
// own stable deep-link routes.

/**
 * Redirect to the canonical first onboarding step.
 *
 * The target is ABSOLUTE (`/me/onboarding/welcome`) so the `*` wildcard can't
 * loop: a relative target on an unknown path resolves against that path
 * (`/me/bad` → `/me/bad/onboarding/welcome` → matches `*` again → …).
 */
function NavigateToWelcome() {
  return <Navigate to="/me/onboarding/welcome" replace />;
}

/**
 * Flag gate for every Homebase surface (contracts §4.1/§6): while the flag
 * read is in flight render a loading placeholder (NEVER a redirect — a flash
 * to onboarding would tear down the requested page for flagged users on every
 * cold load); flag off → the surface stays dark and deep-links land on
 * today's onboarding world; flag on → render the page.
 */
function RequireHomebase({ children }: { children: ReactNode }) {
  const { flags, isLoading } = useMeFeatureFlags();
  if (isLoading) {
    return <SectionLoading label="Loading…" />;
  }
  if (!flags.homebase) {
    return <Navigate to="/me/onboarding/welcome" replace />;
  }
  return <>{children}</>;
}

/**
 * Second gate for the embedded in-call route (codex P2). The embedded call is a
 * separate, per-couple dark rollout on `homebase_embedded_call`; RequireHomebase
 * alone (flags.homebase) would let a homebase-enrolled client deep-link straight
 * to `/me/sessions/:sessionUuid/call` and mount the embed even with the
 * embedded-call flag OFF — bypassing the rollout (we gated the Join button but
 * not the route). Compose this INSIDE RequireHomebase so the route needs BOTH
 * flags. Flag off → redirect to the hub (whose Join opens the video link in a
 * new tab when the embed is off); loading →
 * SectionLoading, never a flash-redirect (mirrors RequireHomebase).
 */
function RequireEmbeddedCall({ children }: { children: ReactNode }) {
  const { flags, isLoading } = useMeFeatureFlags();
  if (isLoading) {
    return <SectionLoading label="Loading…" />;
  }
  if (!flags.homebase_embedded_call) {
    return <Navigate to="/me/home" replace />;
  }
  return <>{children}</>;
}

/**
 * `/me` index fast-path (contracts §4.1): a flagged, fully-onboarded partner
 * re-entering at the index lands on the hub, not back in onboarding. Everyone
 * else keeps today's behavior (onboarding welcome). Holding on SectionLoading
 * while flags resolve keeps the completed+flagged re-entry from bouncing
 * through onboarding on cold loads.
 */
/** The acting partner, tolerant of provider-less renders (the routes suite
 *  mounts MeRoutes bare): no MeContext → no partner → onboarding welcome,
 *  exactly the un-flagged default. */
function useActingPartnerSafe() {
  try {
    return useMeContext().partner;
  } catch {
    return null;
  }
}

function MeIndexRedirect() {
  const { flags, isLoading } = useMeFeatureFlags();
  const partner = useActingPartnerSafe();
  if (isLoading) {
    return <SectionLoading label="Loading…" />;
  }
  if (flags.homebase && partner?.onboardingState.completedAt) {
    return <Navigate to="/me/home" replace />;
  }
  return <NavigateToWelcome />;
}


export function MeRoutes() {
  return (
    <Routes>
      <Route index element={<MeIndexRedirect />} />
      <Route path="onboarding" element={<NavigateToWelcome />} />
      <Route path="onboarding/:step" element={<OnboardingFlow />} />
      <Route
        path="home"
        element={
          <RequireHomebase>
            <HomeHubPage />
          </RequireHomebase>
        }
      />
      <Route
        path="sessions/past"
        element={
          <RequireHomebase>
            <PastSessionsPage />
          </RequireHomebase>
        }
      />
      {/* No session page: the hub's Next Session card holds Join, Reschedule and
          Cancel. The URL stays live because session emails, reminders and booking
          confirmations link here; the link's `?token=` is redeemed by MeAuthGate
          BEFORE this route renders, so the redirect lands signed in. */}
      <Route path="sessions/:sessionUuid" element={<Navigate to="/me/home" replace />} />
      {/* Embedded in-call view. Needs BOTH flags: RequireHomebase (surface) +
          RequireEmbeddedCall (per-couple dark rollout). The gate renders BEFORE
          the Suspense child, so a flag-off deep-link redirects to the hub without ever fetching the lazy Whereby-SDK chunk. */}
      <Route
        path="sessions/:sessionUuid/call"
        element={
          <RequireHomebase>
            <RequireEmbeddedCall>
              <Suspense fallback={<SectionLoading label="Loading…" />}>
                <SessionCallPage />
              </Suspense>
            </RequireEmbeddedCall>
          </RequireHomebase>
        }
      />
      <Route
        path="sessions/:sessionUuid/reschedule"
        element={
          <RequireHomebase>
            <RescheduleOfferFlow />
          </RequireHomebase>
        }
      />
      <Route
        path="reschedule-offers/:offerUuid"
        element={
          <RequireHomebase>
            <ReschedulePickPage />
          </RequireHomebase>
        }
      />
      <Route
        path="reports"
        element={
          <RequireHomebase>
            <ReportsPage />
          </RequireHomebase>
        }
      />
      <Route
        path="reports/:reportUuid"
        element={
          <RequireHomebase>
            <ReportDetailPage />
          </RequireHomebase>
        }
      />
      <Route
        path="checkin/:sessionUuid"
        element={
          <RequireHomebase>
            <CheckinFlow />
          </RequireHomebase>
        }
      />
      <Route
        path="feedback/:sessionUuid"
        element={
          <RequireHomebase>
            <FeedbackFlow />
          </RequireHomebase>
        }
      />
      <Route path="*" element={<NavigateToWelcome />} />
    </Routes>
  );
}
